/**
 * @fileoverview A React component that displays a loading state when no virtual phone numbers are available.
 * The component automatically refreshes every 10 seconds to check for new numbers.
 *
 * @example
 * function ParentComponent() {
 *   const handleRefresh = () => {
 *     // Check for new numbers
 *   };
 *
 *   return <CallNoNumbers onRefresh={handleRefresh} />;
 * }
 */


import { useEffect } from 'react'
import Spinner from '../assets/spinner.svg'

/**
 * @interface CallNoNumbersProps
 * @description Props for the CallNoNumbers component.
 * @property {() => void} onRefresh - The function to call when the component is refreshed.
 */
interface CallNoNumbersProps {
    onRefresh: () => void
}

/**
 * @function CallNoNumbers
 * @description Component for displaying a loading state when no virtual phone numbers are available.
 * @returns {JSX.Element | null} - The CallNoNumbers component.
 */
export function CallNoNumbers({ onRefresh }: CallNoNumbersProps) {
    useEffect(() => {
        const interval = setInterval(() => {
            onRefresh()
        }, 10_000)

        return () => clearInterval(interval)
    }, [])

    return (
        <div className="flex w-[280px] h-[460px] flex-col text-center justify-center gap-y-3 p-6">
            <Spinner className="w-12 h-12 mx-auto animate-spin" />
            <span className="font-medium text-sm">
                No free virtual numbers available at this moment.
            </span>
            <span className="text-xs text-gray-600">
                Wait for a few minutes or try again later.
            </span>
        </div>
    )
}
